import React, { createContext, useContext, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import Plot from 'react-plotly.js';
import IndonesiaChoropleth from './IndonesiaChoropleth';
import '../styles/ReportWorkspace.css';
import '../styles/ReportPreviewSlides.css';
import { brandStatus, fieldStatus, qualityPercent, BRAND_MIN_SCORED } from '../utils/evidenceStatus';
import { brandProvinceValues } from '../utils/provinceValues';
import filterParams from '../utils/filterParams';
const ReportContext = createContext(null);
export const useReport = () => useContext(ReportContext);
const safe = (v) =>
  String(v || 'report')
    .replace(/[^a-z0-9]+/gi, '-')
    .replace(/^-|-$/g, '')
    .toLowerCase();
const numeric = (value) => {
  const match = String(value || '').match(/[-+]?\d+(?:\.\d+)?/);
  return match ? Math.abs(Number(match[0])) : 0;
};
const reportVisual = (section) =>
  section.visual ||
  (/executive summary/i.test(section.title)
    ? 'timeline'
    : /changing|movement/i.test(section.title)
      ? 'columns'
      : /field problem|field priorit|contribution|evidence base/i.test(section.title)
        ? 'table'
        : 'bars');
function SlidePreview({ section, note }) {
  const rows = (section.rows || []).slice(0, 7),
    visual = reportVisual(section),
    max = Math.max(1, ...rows.map((x) => numeric(x.value)));
  const comparisonChart =
    visual === 'evidence' ? (
      <div className="preview-data-table">
        {rows.slice(0, 3).map((x, i) => (
          <div key={i}>
            <b>{x.label}</b>
            <span>{x.value}</span>
          </div>
        ))}
      </div>
    ) : visual === 'multi_timeline' && section.timeline_series?.length ? (
      <Plot
        data={section.timeline_series.map((series, i) => ({
          type: 'scatter',
          mode: 'lines+markers',
          name: series.name,
          x: (series.points || []).map((x) => x.period),
          y: (series.points || []).map((x) => Number(x.conversations || 0)),
          line: { color: ['#225caa', '#c58a20', '#2b7a57'][i % 3] },
        }))}
        layout={{
          autosize: true,
          height: 290,
          margin: { l: 42, r: 12, t: 10, b: 65 },
          legend: { orientation: 'h', y: -0.3 },
          paper_bgcolor: 'transparent',
          plot_bgcolor: 'transparent',
          yaxis: { rangemode: 'tozero' },
        }}
        config={{ displayModeBar: false, responsive: true }}
        useResizeHandler
        style={{ width: '100%' }}
      />
    ) : null;
  const chart =
    visual === 'map' ? (
      <IndonesiaChoropleth
        values={section.map_values || []}
        metric={section.map_metric || 'Conversations'}
        palette={section.map_palette || 'volume'}
        minCount={section.map_min_count || 0}
        height={260}
      />
    ) : visual === 'table' ? (
      <div className="preview-data-table">
        <div>
          <b>Subject</b>
          <b>Evidence</b>
          <b>Status</b>
        </div>
        {rows.map((x, i) => (
          <div key={i}>
            <span>{x.label}</span>
            <span>{x.value}</span>
            <span className={x.status}>
              {x.status === 'red'
                ? 'Review'
                : x.status === 'amber'
                  ? 'Monitor'
                  : x.status === 'green'
                    ? 'Positive'
                    : x.status === 'none'
                      ? '—'
                      : 'Limited'}
            </span>
          </div>
        ))}
      </div>
    ) : visual === 'timeline' && section.timeline?.length ? (
      <Plot
        data={[
          {
            type: 'scatter',
            mode: 'lines+markers',
            x: section.timeline.map((x) => x.period),
            y: section.timeline.map((x) => x.conversations),
            name: 'Conversations',
            line: { color: '#2b7a57' },
          },
        ]}
        layout={{
          autosize: true,
          height: 290,
          margin: { l: 42, r: 12, t: 10, b: 55 },
          showlegend: false,
          paper_bgcolor: 'transparent',
          plot_bgcolor: 'transparent',
        }}
        config={{ displayModeBar: false, responsive: true }}
        useResizeHandler
        style={{ width: '100%' }}
      />
    ) : visual === 'columns' ? (
      <Plot
        data={[
          {
            type: 'bar',
            x: rows.map((x) => x.label),
            y: rows.map((x) => Number(String(x.value || '').match(/[-+]?\d+(?:\.\d+)?/)?.[0] || 0)),
            marker: { color: '#2b7a57' },
          },
        ]}
        layout={{
          autosize: true,
          height: 290,
          margin: { l: 40, r: 10, t: 10, b: 85 },
          showlegend: false,
          paper_bgcolor: 'transparent',
          plot_bgcolor: 'transparent',
        }}
        config={{ displayModeBar: false, responsive: true }}
        useResizeHandler
        style={{ width: '100%' }}
      />
    ) : (
      <div className="preview-bars">
        {rows.map((x, i) => (
          <div key={i}>
            <span>{x.label}</span>
            <i>
              <b style={{ width: `${Math.max(3, (numeric(x.value) * 100) / max)}%` }} />
            </i>
            <strong>{x.value}</strong>
          </div>
        ))}
      </div>
    );
  return (
    <article className="preview-slide">
      <div className="preview-slide-heading">
        <small>{section.page}</small>
        <h4>{section.title}</h4>
        <p>{section.subtitle}</p>
      </div>
      <div className="preview-slide-main">
        <div className="preview-slide-chart">{comparisonChart || chart}</div>
        <div className="preview-slide-notes">
          <strong>What the data shows</strong>
          <p>{section.finding || 'No clear finding in this selection.'}</p>
          {note && <p>{note}</p>}
          {section.next_check && (
            <>
              <strong>Next check</strong>
              <p>{section.next_check}</p>
            </>
          )}
        </div>
      </div>
      <footer>{section.scope}</footer>
    </article>
  );
}
export const downloadBlob = (blob, name) => {
  const url = URL.createObjectURL(blob),
    a = document.createElement('a');
  a.href = url;
  a.download = name;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};
// Names such as "A & B" would otherwise produce an invalid SVG file.
const xml = (v) =>
  String(v ?? '').replace(
    /[&<>"']/g,
    (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&apos;' })[c],
  );
export function downloadInsightGraphic(item) {
  const rows = (item.rows || []).slice(0, 12),
    svg = `<svg xmlns="http://www.w3.org/2000/svg" width="1600" height="900"><rect width="1600" height="900" fill="#fbfaf6"/><rect width="1600" height="18" fill="#1f5eff"/><text x="80" y="95" font-family="Arial" font-size="42" font-weight="700">${xml(item.title)}</text>${rows.map((x, i) => `<text x="90" y="${190 + i * 50}" font-family="Arial" font-size="20">${xml(x.label)}: ${xml(x.value)}</text>`).join('')}</svg>`;
  const b = new Blob([svg], {
    type: 'image/svg+xml',
  });
  downloadBlob(b, `${safe(item.title)}.svg`);
}
export function InsightActions({ item }) {
  const r = useReport();
  return (
    <div className="insight-actions">
      <button onClick={() => downloadInsightGraphic(item)}>Download</button>
      <button className="primary" onClick={() => r.add(item)}>
        Add to report
      </button>
    </div>
  );
}
export function ClipButton({ item }) {
  const reports = useReport();
  return (
    <button
      type="button"
      className="clip-button"
      title="Add this view to Build Report"
      onClick={() => reports.add(item)}
    >
      Clip
    </button>
  );
}
export function ReportsMenu() {
  const r = useReport(),
    [open, setOpen] = useState(false);
  return (
    <div className="reports-menu">
      <button className="reports-menu-button" onClick={() => setOpen((v) => !v)}>
        Reports {r.items.length > 0 && <b>{r.items.length}</b>}
      </button>
      {open && (
        <div className="reports-menu-popover">
          <button
            onClick={() => {
              setOpen(false);
              r.openQuick();
            }}
          >
            <b>Quick Report</b>
            <small>Preview a ready-made report before downloading</small>
          </button>
          <button
            onClick={() => {
              setOpen(false);
              r.setOpen(true);
            }}
          >
            <b>Build Report {r.items.length ? `(${r.items.length})` : ''}</b>
            <small>Combine selected views across pages</small>
          </button>
        </div>
      )}
    </div>
  );
}
export function ReportProvider({ children, filters }) {
  const [items, setItems] = useState([]),
    previewRequest = useRef(0),
    [open, setOpen] = useState(false),
    [quickOpen, setQuickOpen] = useState(false),
    [kind, setKind] = useState('weekly'),
    [quickRange, setQuickRange] = useState('dashboard'),
    [quickFrom, setQuickFrom] = useState(''),
    [quickTo, setQuickTo] = useState(''),
    [preview, setPreview] = useState([]),
    [note, setNote] = useState(''),
    [loading, setLoading] = useState(false),
    [title, setTitle] = useState('Weekly Market Update');
  const globalScope = useMemo(
    () =>
      [
        filters?.date_from && `From ${filters.date_from}`,
        filters?.date_to && `to ${filters.date_to}`,
        !filters?.date_from && filters?.time_range && `Last ${filters.time_range} days`,
        filters?.region_id && `Region ${filters.region_id}`,
      ]
        .filter(Boolean)
        .join(' ') || 'Current dashboard scope',
    [filters],
  );
  const quickScopeFor = (range, from, to) =>
    range === 'dashboard'
      ? globalScope
      : [
          range === 'custom' ? `From ${from || '…'} to ${to || '…'}` : `Last ${range} days`,
          filters?.region_id && `Region ${filters.region_id}`,
        ]
          .filter(Boolean)
          .join(' ');
  useEffect(() => {
    if (quickRange === 'custom') setPreview([]);
  }, [quickRange, quickFrom, quickTo]);
  const params = (range, from, to) => {
    const p = filterParams(filters);
    if (range !== 'dashboard') {
      p.delete('time_range');
      p.delete('date_from');
      p.delete('date_to');
      if (range === 'custom') {
        if (from) p.set('date_from', from);
        if (to) p.set('date_to', to);
      } else p.set('time_range', range);
    }
    return p;
  };
  const build = async (selected, range = quickRange, from = quickFrom, to = quickTo) => {
    if (range === 'custom' && (!from || !to || from > to)) {
      setPreview([]);
      return;
    }
    const requestNumber = ++previewRequest.current;
    setLoading(true);
    try {
      const p = params(range, from, to);
      const scope = quickScopeFor(range, from, to);
      let sections = [];
      if (selected === 'brand') {
        const [r, byRegion] = await Promise.all([
            axios.get(`/api/decision/brands?${p}`),
            axios.get(`/api/sentiment/by-region?${p}&entity_group=product`),
          ]),
          rows = r.data?.brands || [],
          top = rows[0];
        const regional = brandProvinceValues(byRegion.data?.data, top?.brand);
        sections = [
          {
            page: 'Brand Intelligence',
            title: `${top?.brand || 'Leading brand'} across Indonesia`,
            subtitle: 'Scored customer feedback by province',
            scope,
            finding: top
              ? `${top.brand} appears in ${top.total} scored conversations. Positive comments exceed negative comments by ${top.net_sentiment} percentage points, mainly alongside ${top.main_context}.`
              : 'No brand has enough scored evidence.',
            next_check:
              'Open comments in provinces with contrasting sentiment before changing the campaign plan.',
            visual: 'map',
            map_values: regional,
            map_metric: 'Net sentiment',
            map_palette: 'sentiment',
            map_min_count: BRAND_MIN_SCORED,
            rows: rows.slice(0, 10).map((x) => ({
              label: `${x.brand} · ${x.main_context}`,
              value: `Net ${x.net_sentiment > 0 ? '+' : ''}${x.net_sentiment}`,
              status: brandStatus(x),
            })),
          },
          {
            page: 'Brand Intelligence',
            title: 'Brand and crop focus',
            subtitle: top ? `Crops mentioned alongside ${top.brand}` : 'Crop context',
            scope,
            finding: top
              ? `${top.main_context} is the most recorded crop context for ${top.brand}. The crop association reflects conversations, not product use or sales.`
              : 'No crop context recorded.',
            next_check: 'Compare the comments with the field campaign plan and product availability.',
            visual: 'table',
            rows: (top?.contexts || []).map((x) => ({
              label: `${x.crop} · ${top.brand}`,
              value: `${x.conversations} conversations`,
              status: 'none',
            })),
          },
          {
            page: 'Brand Intelligence',
            title: 'Company mentions',
            subtitle: 'Companies named by customers',
            scope,
            finding:
              'These counts show which companies came up in field conversations. They do not measure market share or establish product ownership.',
            next_check:
              'Confirm the product owner in the product master before making a portfolio comparison.',
            visual: 'table',
            rows: Object.values(
              (r.data?.companies || []).reduce((acc, x) => {
                acc[x.company] = acc[x.company] || { name: x.company, count: 0 };
                acc[x.company].count += Number(x.conversations || 0);
                return acc;
              }, {}),
            )
              .sort((a, b) => b.count - a.count)
              .slice(0, 8)
              .map((x) => ({ label: x.name, value: `${x.count} conversations`, status: 'none' })),
          },
        ];
      } else if (selected === 'analytics') {
        const [b, t, a] = await Promise.all([
          axios.get(`/api/decision/brands?${p}`),
          axios.get(`/api/analytics/topics?${p}`),
          axios.get(`/api/agent-view/territory?${p}`),
        ]);
        const brands = [...(b.data?.brands || [])].sort(
          (x, y) => Number(y.total || 0) - Number(x.total || 0),
        );
        const topics = t.data?.topics || [];
        const areas = a.data?.areas || [];
        const activity = Object.fromEntries(
          (a.data?.activity || []).map((x) => [String(x.field_hq_code), x]),
        );
        const products = topics
          .filter((x) => x.topic_group === 'product' || x.topic_group === 'ingredient')
          .sort((x, y) => Number(y.recent || 0) - Number(x.recent || 0));
        const issues = topics
          .filter((x) => x.topic_group === 'issue')
          .sort((x, y) => Number(y.recent_contributors || 0) - Number(x.recent_contributors || 0));
        sections = [
          {
            page: 'Analytics · Marketing',
            title: 'Brand discussion over time',
            subtitle: 'Two most-mentioned scored brands',
            scope,
            visual: 'multi_timeline',
            timeline_series: brands.slice(0, 2).map((x) => ({ name: x.brand, points: x.timeline || [] })),
            finding: brands.length
              ? `${brands[0].brand} was named in ${brands[0].total} scored conversations${brands[1] ? `; ${brands[1].brand} in ${brands[1].total}` : ''}. This is discussion volume, not sales.`
              : 'No scored brand conversations in this selection.',
            next_check: 'Review source comments before changing a brand plan.',
            rows: brands
              .slice(0, 2)
              .map((x) => ({ label: x.brand, value: `${x.total} scored conversations`, status: 'none' })),
          },
          {
            page: 'Analytics · Sales',
            title: 'Field-area contribution',
            subtitle: 'Recorded conversations against onboarding assignments',
            scope,
            visual: 'table',
            finding: `${areas.length} assigned field areas are listed. Recording counts need to be read alongside coverage and working days.`,
            rows: areas.slice(0, 7).map((x) => ({
              label: x.field_hq_name,
              value: `${activity[String(x.field_hq_code)]?.conversations || 0} conversations · ${activity[String(x.field_hq_code)]?.contributors || 0} contributors`,
              status: 'none',
            })),
          },
          {
            page: 'Analytics · Procurement',
            title: 'Products and ingredients discussed',
            subtitle: 'Recent conversation mentions by province',
            scope,
            visual: 'table',
            finding:
              'Mentions are leads for checking demand and inventory; they are not orders or stock-out records.',
            rows: products.slice(0, 7).map((x) => ({
              label: `${x.entity_name} · ${x.province}`,
              value: `${x.recent || 0} recent conversations`,
              status: 'none',
            })),
          },
          {
            page: 'Analytics · Strategy',
            title: 'Issues discussed across regions',
            subtitle: 'Recent conversations and independent field staff',
            scope,
            visual: 'table',
            finding:
              'These issues are named in recorded conversations; the counts do not establish farm incidence.',
            next_check: 'Confirm issue details and recording coverage with local teams.',
            rows: issues.slice(0, 7).map((x) => ({
              label: `${x.entity_name} · ${x.province}`,
              value: `${x.recent || 0} conversations · ${x.recent_contributors || 0} staff`,
              status: 'none',
            })),
          },
        ];
      } else if (selected === 'regional') {
        const r = await axios.get(`/api/decision/region-crop?${p}`),
          rows = r.data?.cells || [],
          top = rows[0];
        sections = [
          {
            page: 'Field Intelligence',
            title: 'Field priorities',
            subtitle: 'Location and crop combinations requiring review',
            scope,
            finding: top
              ? `${top.region_name} → ${top.crop} leads the current list with ${top.need_signals} issue mentions across ${top.conversations} conversations.`
              : 'No field priority has enough evidence.',
            next_check: 'Review comments with the regional team and compare with product availability.',
            rows: rows.slice(0, 10).map((x) => ({
              label: `${x.region_name} → ${x.crop}`,
              value: `${x.conversations} conversations`,
              status: fieldStatus(x),
            })),
          },
          {
            page: 'Field Intelligence',
            title: 'Issues changing now',
            subtitle: 'Emerging and declining conversation attention',
            scope,
            finding: 'Movement reflects discussion frequency, not agronomic severity or sales movement.',
            next_check: 'Check whether movement remains after accounting for recording coverage.',
            rows: rows
              .filter((x) => x.lifecycle !== 'current')
              .slice(0, 10)
              .map((x) => ({
                label: `${x.region_name} → ${x.crop}`,
                value: `${x.lifecycle} ${x.change_percent == null ? '' : `${x.change_percent}%`}`,
                status: x.lifecycle === 'emerging' ? 'amber' : 'grey',
              })),
          },
        ];
      } else if (selected === 'team') {
        const [r, q] = await Promise.all([
            axios.get(`/api/agent-view/stats?${p}`),
            axios.get('/api/agent-view/performance').catch(() => ({
              data: {
                data: [],
              },
            })),
          ]),
          rows = r.data?.data || [],
          quality = q.data?.data || [],
          score = (x) => qualityPercent(x) ?? 0,
          total = rows.reduce((s, x) => s + Number(x.conversation_count || 0), 0),
          avg = quality.length
            ? Math.round(quality.reduce((s, x) => s + score(x.overall_agent_score), 0) / quality.length)
            : null;
        sections = [
          {
            page: 'Field Team',
            title: 'Coverage and contribution',
            subtitle: 'Who captured the market record',
            scope,
            finding: `${rows.length} field team members contributed ${total} conversations in the selected scope.`,
            next_check:
              'Review low coverage against territory opportunity and working days before intervening.',
            rows: rows.slice(0, 12).map((x) => ({
              label: x.agent_name || x.email,
              value: `${x.conversation_count || 0} conversations · ${x.entities_per_conversation || 0} topics/conversation`,
              status: Number(x.conversation_count || 0) >= 20 ? 'green' : 'grey',
            })),
          },
          {
            page: 'Field Team',
            title: 'Conversation quality and coaching',
            subtitle: 'Latest available reviewed scorecards',
            scope,
            finding: quality.length
              ? `${quality.length} team members have reviewed scorecards; the current team average is ${avg}%.`
              : 'No reviewed scorecards are available.',
            next_check: 'Coach using the cited conversations and compare progress in the next review period.',
            rows: quality.slice(0, 12).map((x) => ({
              label: x.agent_name,
              value: `${score(x.overall_agent_score)}% · ${x.coaching_priority || x.primary_gap || 'No coaching focus recorded'}`,
              status:
                score(x.overall_agent_score) >= 75
                  ? 'green'
                  : score(x.overall_agent_score) < 55
                    ? 'red'
                    : 'amber',
            })),
          },
        ];
      } else {
        const [b, f, a, h] = await Promise.all([
            axios.get(`/api/decision/brands?${p}`),
            axios.get(`/api/decision/region-crop?${p}`),
            axios.get(`/api/agent-view/stats?${p}`),
            axios.get(`/api/home/summary?${p}`),
          ]),
          br = b.data?.brands || [],
          fr = f.data?.cells || [],
          issues = f.data?.issues || [],
          agents = a.data?.data || [],
          tb = [...br].sort((x, y) => y.total - x.total)[0],
          tf = fr[0],
          total = agents.reduce((s, x) => s + Number(x.conversation_count || 0), 0);
        sections = [
          {
            page: 'Market Brief',
            title: 'Executive summary',
            subtitle: 'What deserves attention and why',
            visual: 'timeline',
            timeline: (h.data?.trends || []).map((x) => ({
              period: x.week_start,
              conversations: Number(x.conversations || 0),
            })),
            scope,
            finding: [
              tf &&
                `${tf.crop} is being discussed in ${tf.region_name} across ${tf.conversations} conversations, including ${tf.need_signals} recorded concern mentions.`,
              tb &&
                `${tb.brand} appears in ${tb.total} scored conversations, with ${tb.positive} positive and ${tb.negative} negative assessments.`,
            ]
              .filter(Boolean)
              .join(' '),
            next_check:
              'Review the underlying comments before deciding whether a field or brand response is needed.',
            rows: [
              ...fr.slice(0, 4).map((x) => ({
                label: `Field: ${x.region_name} → ${x.crop}`,
                value: `${x.conversations} conversations · ${x.need_signals} issue signals`,
                status: fieldStatus(x),
              })),
              ...br.slice(0, 4).map((x) => ({
                label: `Brand: ${x.brand}`,
                value: `Net ${x.net_sentiment} · ${x.total} scored conversations`,
                status: brandStatus(x),
              })),
            ],
          },
          {
            page: 'Field Intelligence',
            title: 'Reported field problems',
            subtitle: 'Pests, diseases and weeds appearing with crops',
            scope,
            finding: issues[0]
              ? `${issues[0].issue} with ${issues[0].crop} in ${issues[0].region_name} is the most repeated issue context.`
              : 'No issue context is available.',
            next_check:
              'Validate prevalence and decide whether agronomy, marketing or sales should investigate.',
            rows: issues.slice(0, 8).map((x) => ({
              label: `${x.issue} · ${x.crop} · ${x.region_name}`,
              value: `${x.conversations} conversations`,
              status: Number(x.conversations) >= 5 ? 'red' : 'amber',
            })),
          },
          {
            page: 'Market Brief',
            title: 'What is changing',
            subtitle: 'Movement requiring validation',
            scope,
            finding: 'Movement reflects conversation attention, not sales movement or proven causality.',
            next_check: 'Compare with field coverage, inventory and campaign activity.',
            rows: [
              ...fr
                .filter((x) => x.lifecycle === 'emerging')
                .slice(0, 4)
                .map((x) => ({
                  label: `${x.crop} · ${x.region_name}`,
                  value: `Emerging ${x.change_percent ?? ''}%`,
                  status: 'amber',
                })),
              ...br
                .filter((x) => x.lifecycle === 'dying')
                .slice(0, 4)
                .map((x) => ({
                  label: x.brand,
                  value: `Declining ${x.change_percent ?? ''}%`,
                  status: 'red',
                })),
            ],
          },
          {
            page: 'Field Team',
            title: 'Evidence base',
            subtitle: 'Who contributed the underlying market record',
            scope,
            finding: `${agents.length} field team members contributed ${total} conversations in this scope.`,
            next_check:
              'Treat thin coverage as lower confidence and target the next recording effort accordingly.',
            rows: agents.slice(0, 8).map((x) => ({
              label: x.agent_name,
              value: `${x.conversation_count} conversations · ${x.entities_per_conversation} topics/conversation`,
              status: 'grey',
            })),
          },
        ];
      }
      if (requestNumber === previewRequest.current) setPreview(sections);
      return sections;
    } finally {
      if (requestNumber === previewRequest.current) setLoading(false);
    }
  };
  const openQuick = () => {
    setQuickOpen(true);
    setPreview([]);
    setNote('');
    setTimeout(() => build(kind), 0);
  };
  const exportPpt = async (reportTitle, sections) => {
    setLoading(true);
    try {
      const enriched = sections.map((x) => ({
          ...x,
          impact_text: note,
          added_at: new Date().toISOString(),
        })),
        r = await axios.post(
          '/api/reports/powerpoint',
          {
            title: reportTitle,
            template: kind,
            items: enriched,
          },
          {
            responseType: 'blob',
          },
        );
      downloadBlob(r.data, `${safe(reportTitle)}.pptx`);
    } finally {
      setLoading(false);
    }
  };
  const add = (item) => {
    setItems((v) => [
      ...v,
      {
        ...item,
        id: `${Date.now()}-${Math.random()}`,
        scope: item.scope || globalScope,
        added_at: new Date().toISOString(),
      },
    ]);
    setOpen(true);
  };
  const value = {
    items,
    add,
    setOpen,
    openQuick,
  };
  return (
    <ReportContext.Provider value={value}>
      {children}
      {quickOpen && (
        <div className="report-overlay" onClick={() => setQuickOpen(false)}>
          <section className="quick-report-dialog quick-report-preview" onClick={(e) => e.stopPropagation()}>
            <header>
              <div>
                <span>QUICK REPORT</span>
                <h3>Preview before downloading</h3>
              </div>
              <button onClick={() => setQuickOpen(false)}>×</button>
            </header>
            <div className="quick-report-controls">
              <label>
                Report
                <select
                  value={kind}
                  onChange={(e) => {
                    setKind(e.target.value);
                    setPreview([]);
                    build(e.target.value);
                  }}
                >
                  <option value="weekly">Management Update</option>
                  <option value="brand">Brand Intelligence</option>
                  <option value="regional">Field Monitoring</option>
                  <option value="team">Field Team</option>
                  <option value="analytics">Analytics Review</option>
                </select>
              </label>
              <label>
                Period
                <select
                  value={quickRange}
                  onChange={(e) => {
                    setQuickRange(e.target.value);
                    setPreview([]);
                    if (e.target.value !== 'custom') build(kind, e.target.value, quickFrom, quickTo);
                  }}
                >
                  <option value="dashboard">Dashboard period</option>
                  <option value="7">Last 7 days</option>
                  <option value="14">Last 14 days</option>
                  <option value="30">Last 30 days</option>
                  <option value="90">Last 90 days</option>
                  <option value="custom">Choose dates</option>
                </select>
              </label>
              {quickRange === 'custom' && (
                <>
                  <label>
                    From
                    <input type="date" value={quickFrom} onChange={(e) => setQuickFrom(e.target.value)} />
                  </label>
                  <label>
                    To
                    <input type="date" value={quickTo} onChange={(e) => setQuickTo(e.target.value)} />
                  </label>
                  <button
                    type="button"
                    disabled={!quickFrom || !quickTo || quickFrom > quickTo}
                    onClick={() => build(kind, 'custom', quickFrom, quickTo)}
                  >
                    Apply dates
                  </button>
                </>
              )}
              <div className="quick-scope">
                <b>Scope</b>
                <span>{quickScopeFor(quickRange, quickFrom, quickTo)}</span>
              </div>
            </div>
            <div className="report-preview-body">
              {loading && !preview.length ? (
                <p>Building preview…</p>
              ) : (
                preview.map((x, i) => <SlidePreview key={`${x.title}-${i}`} section={x} note={note} />)
              )}
            </div>
            <label>
              Optional management note
              <textarea
                maxLength={240}
                placeholder="Add audience-specific context only if needed."
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </label>
            <footer>
              <button onClick={() => setQuickOpen(false)}>Cancel</button>
              <button
                className="primary"
                disabled={loading || !preview.length}
                onClick={() =>
                  exportPpt(
                    {
                      weekly: 'Management Update',
                      brand: 'Brand Intelligence Report',
                      regional: 'Field Monitoring Report',
                      team: 'Field Team Report',
                      analytics: 'Analytics Review',
                    }[kind],
                    preview,
                  )
                }
              >
                {loading ? 'Preparing…' : `Download ${preview.length}-slide PowerPoint`}
              </button>
            </footer>
          </section>
        </div>
      )}
      {open && (
        <div className="report-overlay" onClick={() => setOpen(false)}>
          <aside className="report-drawer" onClick={(e) => e.stopPropagation()}>
            <header>
              <div>
                <span>REPORT BUILDER</span>
                <h3>Build a management update</h3>
              </div>
              <button onClick={() => setOpen(false)}>×</button>
            </header>
            <label>
              Report title
              <input value={title} onChange={(e) => setTitle(e.target.value)} />
            </label>
            <div className="report-items">
              {items.length ? (
                items.map((x) => (
                  <div className="report-item" key={x.id}>
                    <div className="report-item-content">
                      <b>{x.title}</b>
                      <small>
                        {x.page} · {x.scope}
                      </small>
                      <p>{x.finding}</p>
                    </div>
                    <button onClick={() => setItems((v) => v.filter((i) => i.id !== x.id))}>Remove</button>
                  </div>
                ))
              ) : (
                <p className="report-empty">Use “Add to report” on any view.</p>
              )}
            </div>
            <footer>
              <button onClick={() => setItems([])}>Clear</button>
              <button
                className="primary"
                disabled={!items.length || loading}
                onClick={() => exportPpt(title, items)}
              >
                Download PowerPoint
              </button>
            </footer>
          </aside>
        </div>
      )}
    </ReportContext.Provider>
  );
}
